import { useMemo } from 'react';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { calculateGuaranteeFee } from '@features/booking/utils/calculateFee';

export function usePaymentFee() {
  const booking = useBooking();
  const restaurant = useRestaurant();

  const displayCurrency = restaurant.reservationGuaranteeCurrency ?? restaurant.currency ?? 'USD';

  const selectedSlot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;

  const selectedMenu = selectedSlot?.menus?.find(
    (m) => String(m.id) === booking.selectedExperience?.id,
  ) ?? selectedSlot?.menus?.[0];

  const slotType = selectedSlot?.type ?? booking.guaranteeType ?? 'traditional';

  const feeBreakdown = useMemo(() => {
    if (!selectedSlot) {
      // WL flow or no slot: fee comes from guarantee rules API (booking.feeDeposit)
      const wlFee = booking.feeDeposit ?? 0;
      const gt = booking.guaranteeType;
      const wlType = (gt === 'deposit' || gt === 'no_show') ? gt : null;
      const wlSalesTaxPercent = restaurant.processingSalesTax ?? 0;
      const wlSalesTax = wlSalesTaxPercent > 0 ? Math.round(wlFee * wlSalesTaxPercent) / 100 : 0;
      return {
        guaranteeType: wlType,
        guaranteeSource: wlFee > 0 ? ('slot' as const) : (null as const),
        baseFee: wlFee,
        perPerson: false,
        gratuity: 0,
        gratuityPercent: 0,
        menuTax: 0,
        salesTax: wlSalesTax,
        salesTaxPercent: wlSalesTaxPercent,
        serviceFee: 0,
        serviceFeePercent: 0,
        pairingsFee: 0,
        total: wlFee + wlSalesTax,
        hasBreakdown: wlFee > 0,
        currency: displayCurrency,
        isUSD: !!restaurant.settleUsd,
      };
    }
    const hasMatchingSpecialRequest = !!(
      booking.allergies?.trim() ||
      booking.veggie ||
      booking.kids ||
      booking.pets
    );
    return calculateGuaranteeFee({
      slot: selectedSlot,
      menu: selectedMenu,
      partySize: booking.diners,
      kidsCount: booking.kids ? booking.howManyKids : 0,
      settleUsd: !!restaurant.settleUsd,
      salesTaxPercent: restaurant.processingSalesTax ?? 0,
      restaurantSubdomain: restaurant.subdomainPrefix,
      checkoutProcessingFeePercent: restaurant.checkoutProcessingFeePercent,
      tipChargePercent: booking.tipChargePercent ?? undefined,
      isForeign: booking.isForeign,
      hasGiftCard: !!booking.giftCardToken,
      hasMatchingSpecialRequest,
      rescheduleOriginalType: booking.rescheduleOriginalType,
      selectedPairings: booking.selectedPairings,
      isAgent: booking.isAgentReservation,
      guaranteeCurrency: restaurant.reservationGuaranteeCurrency,
      restaurantCurrency: restaurant.currency,
    });
  }, [selectedSlot, selectedMenu, booking, restaurant, displayCurrency]);

  const cancellationHours = selectedMenu?.cancellationHours;

  return { feeBreakdown, displayCurrency, slotType, cancellationHours };
}
